import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '../lib/api';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  body_plain?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  user_id: string;
}

interface GmailConnection {
  id: string;
  is_connected: boolean;
  gmail_email: string;
  display_name: string;
  is_primary: boolean;
  needs_refresh: boolean;
  token_status: string;
  /** SKY-469: true when the grant lacks gmail.readonly — send works, sync won't. */
  missing_read_scope: boolean;
  created_at: string;
}

interface CampaignAnalytics {
  templatesCreated: number;
  emailsSent: number;
  successfulSends: number;
  failedSends: number;
  successRate: number;
  lastCampaignDate: string | null;
}


interface CampaignContextType {
  // Email Templates
  templates: EmailTemplate[];
  isLoadingTemplates: boolean;
  templatesError: string | null;
  refreshTemplates: () => Promise<void>;
  addTemplate: (template: EmailTemplate) => void;
  updateTemplate: (templateId: string, updates: Partial<EmailTemplate>) => void;
  removeTemplate: (templateId: string) => void;

  // Gmail Connections
  gmailConnections: GmailConnection[];
  isLoadingConnections: boolean;
  connectionsError: string | null;
  refreshConnections: () => Promise<void>;
  addConnection: (connection: GmailConnection) => void;
  updateConnection: (connectionId: string, updates: Partial<GmailConnection>) => void;
  removeConnection: (connectionId: string) => void;
  
  // Analytics
  analytics: CampaignAnalytics;
  isLoadingAnalytics: boolean;
  analyticsError: string | null;
  refreshAnalytics: () => Promise<void>;
  onEmailSent: () => void; // Trigger analytics refresh after email is sent
  
  // Helper methods
  getPrimaryConnection: () => GmailConnection | undefined;
  getActiveConnections: () => GmailConnection[];
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

export const useCampaignContext = () => {
  const context = useContext(CampaignContext);
  if (context === undefined) {
    throw new Error('useCampaignContext must be used within a CampaignProvider');
  }
  return context;
};

interface CampaignProviderProps {
  children: React.ReactNode;
}

export const CampaignProvider: React.FC<CampaignProviderProps> = ({ children }) => {
  // Email Templates State
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  // Gmail Connections State
  const [gmailConnections, setGmailConnections] = useState<GmailConnection[]>([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [connectionsError, setConnectionsError] = useState<string | null>(null);

  // Analytics State
  const [analytics, setAnalytics] = useState<CampaignAnalytics>({
    templatesCreated: 0,
    emailsSent: 0,
    successfulSends: 0,
    failedSends: 0,
    successRate: 0,
    lastCampaignDate: null
  });
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  // Load Templates
  const refreshTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      setTemplatesError(null);
      const response = await apiClient.getEmailTemplates();
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
      setTemplatesError('Failed to load email templates');
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Load Gmail Connections
  const refreshConnections = async () => {
    try {
      setIsLoadingConnections(true);
      setConnectionsError(null);
      const response = await apiClient.getAllGmailConnections();
      setGmailConnections(response.connections || []);
    } catch (error) {
      console.error('Failed to load Gmail connections:', error);
      setConnectionsError('Failed to load Gmail connections');
    } finally {
      setIsLoadingConnections(false);
    }
  };

  // Load Analytics
  const refreshAnalytics = async () => {
    try {
      setIsLoadingAnalytics(true);
      setAnalyticsError(null);
      
      // Get analytics summary from backend (calculated with SQL)
      const summary = await apiClient.getCampaignAnalyticsSummary();
      
      setAnalytics({
        templatesCreated: summary.templates_created,
        emailsSent: summary.emails_sent,
        successfulSends: summary.successful_sends,
        failedSends: summary.failed_sends,
        successRate: summary.success_rate,
        lastCampaignDate: summary.last_campaign_date
      });
    } catch (error) {
      console.error('Failed to load analytics:', error);
      setAnalyticsError('Failed to load campaign analytics');
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  // Template CRUD operations
  const addTemplate = (template: EmailTemplate) => {
    setTemplates(prev => [template, ...prev]);
  };

  const updateTemplate = (templateId: string, updates: Partial<EmailTemplate>) => {
    setTemplates(prev => 
      prev.map(template => 
        template.id === templateId ? { ...template, ...updates } : template
      )
    );
  };

  const removeTemplate = (templateId: string) => {
    setTemplates(prev => prev.filter(template => template.id !== templateId));
  };

  // Gmail Connection CRUD operations
  const addConnection = (connection: GmailConnection) => {
    setGmailConnections(prev => [connection, ...prev]);
  };

  const updateConnection = (connectionId: string, updates: Partial<GmailConnection>) => {
    setGmailConnections(prev =>
      prev.map(connection =>
        connection.id === connectionId ? { ...connection, ...updates } : connection
      )
    );
  };

  const removeConnection = (connectionId: string) => {
    setGmailConnections(prev => prev.filter(connection => connection.id !== connectionId));
  };

  // Helper methods
  const getPrimaryConnection = () => {
    return gmailConnections.find(conn => conn.is_primary);
  };

  const getActiveConnections = () => {
    // Return all connections - the backend will handle token refresh during sending
    // including refreshing expired 1-hour tokens and prompting re-auth for 7-day expiration
    return gmailConnections.filter(conn => conn.token_status === 'active');
  };

  // Callback for when an email is sent - refresh analytics
  const onEmailSent = () => {
    // Refresh analytics in the background without affecting loading state
    refreshAnalytics();
  };

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        refreshTemplates(),
        refreshConnections(),
        refreshAnalytics()
      ]);
    };

    loadData();
  }, []);

  const contextValue: CampaignContextType = {
    // Email Templates
    templates,
    isLoadingTemplates,
    templatesError,
    refreshTemplates,
    addTemplate,
    updateTemplate,
    removeTemplate,

    // Gmail Connections
    gmailConnections,
    isLoadingConnections,
    connectionsError,
    refreshConnections,
    addConnection,
    updateConnection,
    removeConnection,

    // Analytics
    analytics,
    isLoadingAnalytics,
    analyticsError,
    refreshAnalytics,
    onEmailSent,

    // Helper methods
    getPrimaryConnection,
    getActiveConnections
  };

  return (
    <CampaignContext.Provider value={contextValue}>
      {children}
    </CampaignContext.Provider>
  );
};